/**
 * ============================================================================
 * TRANSIENT: Appointment Config Page (appointment-config-page)
 * Type: poc
 * Amplitude key: none
 * End date: 2026-12-31
 * PR: #TODO_AFTER_PR_CREATION_appointment-config-page
 * ============================================================================
 */

import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

import {
    isConfigValid,
    getDefaultSecondSlot,
    minutesToTime,
    timeToMinutes,
    validatePeriods,
} from '../../resources/appointment/operations';
import type {
    ContactType,
    DayKey,
    InternalDayState,
    Period,
} from '../../resources/appointment/operations';
import {
    useGetAvailabilityConfig,
    useGetCalendarStatus,
} from '../../resources/appointment/queries';
import { usePutAvailabilityConfig } from '../../resources/appointment/mutations';
import type {
    AvailabilityConfig,
    AvailabilityConfigPayload,
} from '../../resources/appointment/types';

export type AppointmentTab = 'disponibilites' | 'rendez-vous';
type AppointmentSection = 'slots' | 'contact' | 'params';

function configToInternalDays(
    availabilityPeriods: Record<DayKey, Period[]>,
): Record<DayKey, InternalDayState> {
    const allDays: Record<DayKey, InternalDayState> = {
        monday: { active: false, periods: [] },
        tuesday: { active: false, periods: [] },
        wednesday: { active: false, periods: [] },
        thursday: { active: false, periods: [] },
        friday: { active: false, periods: [] },
        saturday: { active: false, periods: [] },
        sunday: { active: false, periods: [] },
    };
    for (const [key, periods] of Object.entries(availabilityPeriods)) {
        allDays[key as DayKey] = { active: periods.length > 0, periods };
    }
    return allDays;
}

function buildPeriods(days: Record<DayKey, InternalDayState>): Record<DayKey, Period[]> {
    return Object.fromEntries(
        (Object.entries(days) as [DayKey, InternalDayState][]).map(([k, v]) => {
            if (!v.active) return [k, []];
            const validPeriods = v.periods.filter(
                (p): p is Period => p.startTime !== undefined && p.endTime !== undefined,
            );
            return [k, validPeriods];
        }),
    ) as Record<DayKey, Period[]>;
}

function hasAnySlotError(days: Record<DayKey, InternalDayState>): boolean {
    return Object.values(days).some(
        (state) =>
            state.active &&
            state.periods.length > 0 &&
            Object.keys(validatePeriods(state.periods)).length > 0,
    );
}

const buildDefaultDays = (): Record<DayKey, InternalDayState> => ({
    monday: { active: true, periods: [{ startTime: '09:00', endTime: '18:00' }] },
    tuesday: { active: true, periods: [{ startTime: '09:00', endTime: '18:00' }] },
    wednesday: { active: true, periods: [{ startTime: '09:00', endTime: '18:00' }] },
    thursday: { active: true, periods: [{ startTime: '09:00', endTime: '18:00' }] },
    friday: { active: true, periods: [{ startTime: '09:00', endTime: '18:00' }] },
    saturday: { active: false, periods: [] },
    sunday: { active: false, periods: [] },
});

function parseServerConfig(config: AvailabilityConfig): {
    serverDays: Record<DayKey, InternalDayState>;
    payload: AvailabilityConfigPayload;
} {
    const serverDays = configToInternalDays(config.availabilityPeriods);
    const { defaultCallType, slotDurationInMinutes, timezone, travelerReminder } = config;
    return {
        serverDays,
        payload: {
            availabilityPeriods: buildPeriods(serverDays),
            defaultCallType,
            slotDurationInMinutes,
            timezone,
            travelerReminder,
        },
    };
}

export function useAppointment() {
    const { data: calendarStatus, isLoading: isCalendarStatusLoading } = useGetCalendarStatus();
    const { data: configData, isLoading: isConfigLoading } = useGetAvailabilityConfig();
    const isConnected = calendarStatus?.google === 'connected';
    const isLoading = isCalendarStatusLoading || isConfigLoading;

    const [activeTab, setActiveTab] = useState<AppointmentTab>('disponibilites');
    const [days, setDays] = useState<Record<DayKey, InternalDayState>>(buildDefaultDays);
    const [defaultCallType, setDefaultCallType] = useState<ContactType>('audio_request_manager');
    const [slotDurationInMinutes, setSlotDurationInMinutes] = useState<15 | 30 | 45 | 60>(30);
    const [travelerReminder, setTravelerReminder] = useState(true);
    const [timezone, setTimezone] = useState(
        () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    );
    const [activeSection, setActiveSection] = useState<AppointmentSection>('slots');
    const [lastSavedAt, setLastSavedAt] = useState<Date>(() => new Date());
    const [, setTick] = useState(0);

    const slotsRef = useRef<HTMLDivElement>(null);
    const contactRef = useRef<HTMLDivElement>(null);
    const paramsRef = useRef<HTMLDivElement>(null);
    const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastSavedConfigRef = useRef<AvailabilityConfigPayload | null>(null);

    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 60_000);
        return () => clearInterval(id);
    }, []);

    /* eslint-disable react-hooks/set-state-in-effect -- initializing state from server data on first load */
    useEffect(() => {
        if (!configData) return;
        const { serverDays, payload } = parseServerConfig(configData);
        lastSavedConfigRef.current = payload;
        setDays(serverDays);
        setDefaultCallType(payload.defaultCallType);
        setSlotDurationInMinutes(payload.slotDurationInMinutes);
        setTimezone(payload.timezone);
        setTravelerReminder(payload.travelerReminder);
        setLastSavedAt(new Date(configData.updatedAt));
    }, [configData]);
    /* eslint-enable react-hooks/set-state-in-effect */

    useEffect(() => {
        if (!isConnected) return;
        const els = [
            { id: 'slots' as AppointmentSection, el: slotsRef.current },
            { id: 'contact' as AppointmentSection, el: contactRef.current },
            { id: 'params' as AppointmentSection, el: paramsRef.current },
        ];
        const observer = new IntersectionObserver(
            (entries) =>
                entries.forEach((e) => {
                    if (e.isIntersecting) setActiveSection(e.target.id as AppointmentSection);
                }),
            { rootMargin: '-20% 0px -70% 0px' },
        );
        els.forEach(({ el }) => el && observer.observe(el));
        return () => observer.disconnect();
    }, [isConnected]);

    const { mutate: saveConfig } = usePutAvailabilityConfig();

    useEffect(() => {
        if (!isConnected) return;
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => {
            if (hasAnySlotError(days)) return;
            const periods = buildPeriods(days);
            if (!isConfigValid(periods)) return;
            const payload: AvailabilityConfigPayload = {
                availabilityPeriods: periods,
                defaultCallType,
                slotDurationInMinutes,
                timezone,
                travelerReminder,
            };
            if (JSON.stringify(payload) === JSON.stringify(lastSavedConfigRef.current)) return;
            saveConfig(payload, {
                onSuccess: (data) => {
                    setLastSavedAt(new Date(data.updatedAt));
                    lastSavedConfigRef.current = payload;
                },
            });
        }, 1500);
        return () => {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        };
    }, [days, defaultCallType, slotDurationInMinutes, timezone, travelerReminder, isConnected]);

    const handleToggleDay = (dayKey: DayKey, active: boolean) => {
        setDays((prev) => {
            const next = structuredClone(prev);
            next[dayKey].active = active;
            if (active && next[dayKey].periods.length === 0)
                next[dayKey].periods = [{ startTime: '09:00', endTime: '18:00' }];
            return next;
        });
    };

    const handleSlotStartChange = (dayKey: DayKey, slotIdx: number, newStart: string) => {
        setDays((prev) => {
            const next = structuredClone(prev);
            const period = next[dayKey].periods[slotIdx];
            period.startTime = newStart;
            // Auto-adjust end when start moves past it (min gap = 15 min)
            if (
                period.endTime !== undefined &&
                timeToMinutes(period.endTime) < timeToMinutes(newStart) + 15
            ) {
                period.endTime = minutesToTime(
                    Math.min(timeToMinutes(newStart) + 120, 23 * 60 + 45),
                );
            }
            return next;
        });
    };

    const handleSlotEndChange = (dayKey: DayKey, slotIdx: number, newEnd: string) => {
        setDays((prev) => {
            const next = structuredClone(prev);
            next[dayKey].periods[slotIdx].endTime = newEnd;
            return next;
        });
    };

    const handleDeleteSlot = (dayKey: DayKey, slotIdx: number) => {
        setDays((prev) => {
            const next = structuredClone(prev);
            next[dayKey].periods.splice(slotIdx, 1);
            if (next[dayKey].periods.length === 0) next[dayKey].active = false;
            return next;
        });
    };

    const handleAddSlot = (dayKey: DayKey) => {
        setDays((prev) => {
            const next = structuredClone(prev);
            const periods = next[dayKey].periods;
            const lastEnd = periods[periods.length - 1]?.endTime;
            periods.push(
                lastEnd ? getDefaultSecondSlot(lastEnd) : { startTime: '09:00', endTime: '18:00' },
            );
            return next;
        });
    };

    const handleDurationChange = (newDuration: 15 | 30 | 45 | 60) => {
        setSlotDurationInMinutes(newDuration);
    };

    const handleSectionClick = (section: AppointmentSection) => {
        const map: Record<AppointmentSection, RefObject<HTMLDivElement>> = {
            slots: slotsRef,
            contact: contactRef,
            params: paramsRef,
        };
        map[section].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const activeDays = Object.fromEntries(
        Object.entries(days).map(([k, v]) => [k, v.active]),
    ) as Record<DayKey, boolean>;

    const hasNoSlots = !Object.values(days).some((v) => v.active && v.periods.length > 0);

    return {
        isLoading,
        isConnected: !!isConnected,
        activeTab,
        days,
        defaultCallType,
        slotDurationInMinutes,
        travelerReminder,
        timezone,
        activeSection,
        activeDays,
        hasNoSlots,
        lastSavedAt,
        slotsRef,
        contactRef,
        paramsRef,
        setActiveTab,
        setDefaultCallType,
        setTravelerReminder,
        setTimezone,
        handleToggleDay,
        handleSlotStartChange,
        handleSlotEndChange,
        handleDeleteSlot,
        handleAddSlot,
        handleDurationChange,
        handleSectionClick,
    };
}
